import _ from 'lodash';
import React, { PropTypes } from 'react';

// columnrangeにはhighcharts-moreが必要
import ChartMore from '../../../../../../../utils/components/ChartMore';
import Chart from '../../../../../../../utils/components/Chart';
import safeToFixer from '../utils/safeToFixer';
import isDecimalEqual from '../utils/isDecimalEqual';
import { getMinGTMultiple } from '../utils/integerUtility';
import getTimeRangeData from '../utils/getTimeRangeData';
import message from '../../../../../../../utils/message';

const getRangedTimeAndScoreData = (data) => {
  const times = data.map(({ time }) => time);
  const {
    rangeData,
    min,
    max,
    width,
  } = getTimeRangeData(times);

  const rangedTimeAndScoreData = rangeData.map(({ bottom, top, middle }) => {
    const concernedData = _.filter(data, ({ time }) => {
      if (top === max) {
        // 最大値は一番上の分布に含む
        return bottom <= time;
      }
      return (bottom <= time) && (time < top);
    });

    const count = concernedData.length;

    const concernedDataScores = concernedData.map(({ score }) => score);
    const maxScore = _.max(concernedDataScores);
    const minScore = _.min(concernedDataScores);
    const averageScore = _.mean(concernedDataScores);

    return {
      bottom,
      top,
      middle,
      count,
      minScore,
      maxScore,
      averageScore,
    };
  });

  return {
    rangedTimeAndScoreData,
    min,
    max,
    width,
  };
};

const getPerfectScore = (scores) => {
  const dataMax = _.max(scores);
  if (dataMax <= 100) {
    return 100;
  }
  // dataMax以上で、最小の100の倍数を得る
  return getMinGTMultiple(dataMax, 100);
};

const LearningTimeAndTestScoreChart = ({ toFixedNum, data }) => {
  const {
    rangedTimeAndScoreData,
    min,
    max,
    width,
  } = getRangedTimeAndScoreData(data);

  const scoreMinMaxData =
    rangedTimeAndScoreData.map(({ middle, minScore, maxScore }) => [middle, minScore, maxScore]);

  const scoreAverageData =
    rangedTimeAndScoreData.map(({ middle, averageScore }) => [middle, averageScore]);

  const learningTimeHistogramData =
    rangedTimeAndScoreData.map(({ middle, count }) => [middle, count]);

  // 満点
  const perfectScore = getPerfectScore(data.map(({ score }) => score));

  const commonOptions = {
    chart: {
      marginLeft: 72, // Keep all charts left aligned
      spacingTop: 20,
      spacingBottom: 20,
    },
    legend: {
      enabled: false,
    },
    title: {
      style: {
        fontSize: '24px',
      },
    },
    xAxis: {
      minPadding: 0.1,
      maxPadding: 0.1,
      startOnTick: true,
      endOnTick: true,
      tickPositions: [...(_.range(min, max, width)).map(e => _.round(e)), max],
      title: {
        text: message.learnTimeHour,
      },
    },
  };

  const getFormatter = (needsScoreRange = true) => {
    function formatter() {
      const selectedData = _.find(rangedTimeAndScoreData, ({ middle }) => isDecimalEqual(middle, this.x, 1));
      if (!selectedData) {
        return '';
      }

      const { bottom, top, minScore, maxScore, count, averageScore } = selectedData;

      const rangeMessage = `${bottom}${message.hour}〜${top}${message.hour}`;

      const scoreRange = (count >= 1 && needsScoreRange)
        ? `<br/>${message.average}: <b>${safeToFixer(averageScore, toFixedNum)}</b><br/><br/>${message.max}: <b>${maxScore}</b><br/>${message.min}: <b>${minScore}</b>`
        : '';

      return `<span>${rangeMessage}</span><br/>${message.numOfStudents}: <b>${count}</b>${scoreRange}`;
    }

    return formatter;
  };


  const scoreChartOptions = _.merge({}, commonOptions, {
    title: {
      text: message.learnTimeVsTestScore,
    },
    yAxis: {
      min: 0,
      max: perfectScore,
      tickAmount: 6,
      title: {
        text: message.score,
      },
    },
    plotOptions: {
      line: {
        dataLabels: {
          enabled: true,
          y: -12,
          formatter() {
            return (isFinite(this.y)) ? safeToFixer(this.y, toFixedNum) : '';
          },
        },
        enableMouseTracking: false,
      },
      columnrange: {
        grouping: false,
        shadow: false,
      },
    },
    tooltip: {
      headerFormat: '',
      formatter: getFormatter(true),
      shared: true,
    },
    series: [
      {
        type: 'columnrange',
        name: `${message.min}${message.max}`,
        data: scoreMinMaxData,
        pointWidth: 20,
        color: '#FFB973',
      },
      {
        name: message.score,
        type: 'line',
        data: scoreAverageData,
      },
    ],
  });

  const learningTimeHistogramOptions = _.merge({}, commonOptions, {
    chart: {
      type: 'column',
    },
    title: {
      text: message.learnTimeDistribution,
    },
    tooltip: {
      headerFormat: '',
      formatter: getFormatter(false),
      shared: true,
    },
    yAxis: {
      min: 0,
      // 小数禁止
      allowDecimals: false,
      title: {
        text: message.numOfStudents,
      },
    },
    series: [{
      name: message.numOfStudents,
      color: '#d4f59c',
      data: learningTimeHistogramData,
      pointPadding: 0,
      groupPadding: 0,
    }],
  });

  return (
    <div>
      <ChartMore options={scoreChartOptions} />
      <Chart options={learningTimeHistogramOptions} />
    </div>
  );
};

LearningTimeAndTestScoreChart.propTypes = {
  data: PropTypes.array.isRequired,
  toFixedNum: PropTypes.number.isRequired,
};

export default LearningTimeAndTestScoreChart;



// WEBPACK FOOTER //
// ./es6/admin-common-js/apps/csv_chart/charts/portfolio/analysis/components/LearningTimeAndTestScoreChart.js